// src/data/assessmentProfiles.js — the eight assessment archetypes.
// Shared by the assessment component and the Dashboard results card;
// each profile carries strengths, key questions, module and tools.
import {
  Brain, Target, Eye, Heart, Users, Lightbulb, Scale, Mountain
} from 'lucide-react';

export const PROFILES = {
  criticalThinker: {
    id: 'criticalThinker',
    title: 'The Questioner',
    icon: Brain,
    color: 'blue',
    description: 'You have a natural instinct to dig beneath surface claims and challenge assumptions. You\'re drawn to the Socratic method of questioning and want to think more clearly about everything.',
    strengths: ['Spotting hidden assumptions', 'Asking probing questions', 'Intellectual curiosity', 'Challenging the status quo'],
    module: 'Module 1: What Is Philosophy?',
    keyQuestions: [
      'What assumptions am I making without realising?',
      'How can I question my beliefs more effectively?',
      'What\'s the difference between knowledge and opinion?'
    ],
    recommendedTools: ['Socratic Dialogue Journal', 'Assumption Excavator']
  },
  logicMaster: {
    id: 'logicMaster',
    title: 'The Logic Master',
    icon: Target,
    color: 'green',
    description: 'You love clear reasoning and get frustrated by sloppy arguments. You\'re drawn to formal logic, spotting fallacies, and building airtight arguments.',
    strengths: ['Identifying logical fallacies', 'Constructing valid arguments', 'Analysing reasoning', 'Precision thinking'],
    module: 'Module 2: Logic & Clear Thinking',
    keyQuestions: [
      'What makes an argument valid vs sound?',
      'How can I spot when someone is reasoning badly?',
      'How do I build convincing, logical arguments?'
    ],
    recommendedTools: ['Fallacy Detective', 'Argument Builder']
  },
  truthSeeker: {
    id: 'truthSeeker',
    title: 'The Truth Seeker',
    icon: Eye,
    color: 'purple',
    description: 'You care deeply about what\'s true and how we can know it. You\'re sceptical of claims without evidence and fascinated by epistemology, the study of knowledge itself.',
    strengths: ['Evaluating evidence', 'Assessing source credibility', 'Epistemic humility', 'Truth-seeking'],
    module: 'Module 3: Epistemology',
    keyQuestions: [
      'How do I know what I know?',
      'How can I tell if a source is credible?',
      'When should I be confident vs uncertain in my beliefs?'
    ],
    recommendedTools: ['CREDIBLE Framework Evaluator', 'Epistemic Confidence Calibrator']
  },
  moralCompass: {
    id: 'moralCompass',
    title: 'The Moral Compass',
    icon: Heart,
    color: 'red',
    description: 'You\'re deeply concerned with right and wrong, and want to make ethical decisions systematically. You\'re drawn to moral philosophy and applying ethical frameworks to real dilemmas.',
    strengths: ['Ethical reasoning', 'Recognising moral complexity', 'Values alignment', 'Principled decision-making'],
    module: 'Module 4: Ethics & Moral Philosophy',
    keyQuestions: [
      'What makes an action right or wrong?',
      'How should I approach moral dilemmas?',
      'Am I living according to my values?'
    ],
    recommendedTools: ['Ethical Framework Analyser', 'Personal Ethics Auditor']
  },
  identityExplorer: {
    id: 'identityExplorer',
    title: 'The Identity Explorer',
    icon: Users,
    color: 'cyan',
    description: 'You\'re fascinated by questions of personal identity and free will. What makes you "you"? Are you in control of your choices? These metaphysical questions captivate you.',
    strengths: ['Self-reflection', 'Metaphysical thinking', 'Questioning identity', 'Agency awareness'],
    module: 'Module 5: Metaphysics & Reality',
    keyQuestions: [
      'What makes me "me" over time?',
      'Do I have free will or is everything determined?',
      'How much control do I really have over my life?'
    ],
    recommendedTools: ['Personal Identity Explorer', 'Free Will Experience Tracker']
  },
  consciousnessInvestigator: {
    id: 'consciousnessInvestigator',
    title: 'The Consciousness Investigator',
    icon: Lightbulb,
    color: 'yellow',
    description: 'You\'re intrigued by the mystery of consciousness and subjective experience. The mind-body problem, AI consciousness, and qualia fascinate you.',
    strengths: ['Phenomenological awareness', 'Mind-body curiosity', 'Thought experiments', 'Questioning consciousness'],
    module: 'Module 6: Philosophy of Mind',
    keyQuestions: [
      'What is consciousness and how does it arise?',
      'Could a computer ever be truly conscious?',
      'What is it like to have my particular experiences?'
    ],
    recommendedTools: ['Qualia Investigation Journal', 'Chinese Room Interactive']
  },
  justiceAdvocate: {
    id: 'justiceAdvocate',
    title: 'The Justice Advocate',
    icon: Scale,
    color: 'indigo',
    description: 'You care deeply about fairness, justice, and how society should be organised. Political philosophy and questions of equality and rights energise you.',
    strengths: ['Recognising injustice', 'Political reasoning', 'Fairness thinking', 'Social consciousness'],
    module: 'Module 7: Social & Political Philosophy',
    keyQuestions: [
      'What makes a society just?',
      'How should we balance freedom and equality?',
      'What political system aligns with my values?'
    ],
    recommendedTools: ['Veil of Ignorance Analyser', 'Political Philosophy Mapper']
  },
  meaningMaker: {
    id: 'meaningMaker',
    title: 'The Meaning Maker',
    icon: Mountain,
    color: 'orange',
    description: 'You grapple with existential questions about meaning, purpose, and how to live authentically. Existentialism and questions of life\'s meaning resonate deeply with you.',
    strengths: ['Existential awareness', 'Authenticity seeking', 'Meaning creation', 'Philosophical depth'],
    module: 'Module 8: Existentialism & Meaning',
    keyQuestions: [
      'What gives life meaning?',
      'Am I living authentically or in bad faith?',
      'How do I create purpose in an absurd universe?'
    ],
    recommendedTools: ['Existential Choice Analyser', 'Meaning Creation Workshop']
  }
};
